// src/components/Separation/TerminationModal.tsx

import React, { useState } from 'react';
import { toast } from "sonner";
import { TerminationCreate } from '../../types/separation';
import { terminateEmployee } from '../../api/separation';

interface TerminationModalProps {
  isOpen: boolean;
  onClose: () => void;
  employeeId?: string;
  employeeName?: string;
  onSuccess: () => void;
}

const TerminationModal: React.FC<TerminationModalProps> = ({
  isOpen,
  onClose,
  employeeId,
  employeeName = 'Employee',
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<TerminationCreate>({
    employee_id: employeeId || '',
    termination_date: new Date().toISOString().split('T')[0],
    last_working_day: '',
    termination_type: 'INVOLUNTARY',
    reason: '',
    additional_notes: '',
    notice_served: false,
    notice_waived: false,
    notice_pay_in_lieu: false,
    show_cause_issued: false,
    show_cause_response: '',
    eligible_for_rehire: true,
  });

  if (!isOpen) return null;

  const handleSubmit = async () => {
    if (!formData.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    if (!formData.reason) {
      toast.error('Please provide a termination reason');
      return;
    }
    if (!formData.last_working_day) {
      toast.error('Please provide last working day');
      return;
    }

    setLoading(true);
    try {
      await terminateEmployee(formData);
      toast.success('Employee terminated successfully');
      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.message || 'Failed to terminate employee');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof TerminationCreate, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 opacity-75" onClick={onClose}></div>
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Terminate Employee</h3>
                <p className="text-sm text-gray-500 mt-1">Employee: {employeeName}</p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Termination Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Termination Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.termination_date}
                  onChange={(e) => handleInputChange('termination_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Working Day <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.last_working_day}
                  onChange={(e) => handleInputChange('last_working_day', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Termination Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.termination_type}
                onChange={(e) => handleInputChange('termination_type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
              >
                <option value="VOLUNTARY">Voluntary</option>
                <option value="INVOLUNTARY">Involuntary</option>
                <option value="MUTUAL">Mutual Agreement</option>
                <option value="CONTRACT_END">Contract End</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Termination <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.reason}
                onChange={(e) => handleInputChange('reason', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="Provide detailed reason for termination..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
              <textarea
                value={formData.additional_notes}
                onChange={(e) => handleInputChange('additional_notes', e.target.value)}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500"
                placeholder="Any additional information..."
              />
            </div>

            {/* Notice Details */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold text-gray-900 mb-3">Notice Details</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.notice_served}
                    onChange={(e) => handleInputChange('notice_served', e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Notice Served</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.notice_waived}
                    onChange={(e) => handleInputChange('notice_waived', e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Notice Waived</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.notice_pay_in_lieu}
                    onChange={(e) => handleInputChange('notice_pay_in_lieu', e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Notice Pay in Lieu</span>
                </label>
              </div>
            </div>

            {/* Show Cause */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-semibold text-gray-900 mb-3">Show Cause Details</h4>
              <div className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.show_cause_issued}
                    onChange={(e) => handleInputChange('show_cause_issued', e.target.checked)}
                    className="w-4 h-4 text-red-600 rounded"
                  />
                  <span className="text-sm text-gray-700">Show Cause Issued</span>
                </label>
                {formData.show_cause_issued && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Show Cause Response</label>
                    <textarea
                      value={formData.show_cause_response}
                      onChange={(e) => handleInputChange('show_cause_response', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      placeholder="Employee's response to show cause notice..."
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Rehire Eligibility */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Eligible for Rehire
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.eligible_for_rehire === true}
                    onChange={() => handleInputChange('eligible_for_rehire', true)}
                    className="w-4 h-4 text-green-600"
                  />
                  <span className="text-sm text-gray-700">Yes</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={formData.eligible_for_rehire === false}
                    onChange={() => handleInputChange('eligible_for_rehire', false)}
                    className="w-4 h-4 text-red-600"
                  />
                  <span className="text-sm text-gray-700">No</span>
                </label>
              </div>
            </div>
          </div>

          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t border-gray-200 flex gap-3">
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
            >
              {loading ? 'Processing...' : 'Terminate Employee'}
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TerminationModal;